<template>
  <div class="qhls-exam-center">
    <div class="page-header">
      <p class="page-subtitle mal-text">QHLS പരീക്ഷാ കേന്ദ്ര വിവരങ്ങൾ നൽകുക</p>
    </div>

    <!-- State/District admin: pick district/zone/unit (NOT needed when opened from Unit route) -->
    <div v-if="showStateDistrictPickers" class="form-group">
      <label class="form-label mal-text">ജില്ല (District) <span class="text-danger">*</span></label>
      <select v-model="selectedDistrictId" class="form-control" :disabled="userLevel !== 1">
        <option value="" disabled class="mal-text">ജില്ല തിരഞ്ഞെടുക്കുക</option>
        <option v-for="d in districts" :key="'d-'+d.id" :value="String(d.id)">{{ d.name }}</option>
      </select>
      <small v-if="districtsLoading" class="text-muted mal-text d-block mt-1">ജില്ലകൾ ലോഡ് ചെയ്യുന്നു...</small>
    </div>

    <div v-if="showStateDistrictPickers" class="form-group">
      <label class="form-label mal-text">സോൺ (Zone) <span class="text-danger">*</span></label>
      <select v-model="selectedZoneId" class="form-control" :disabled="zonesLoading || !effectiveDistrictId">
        <option value="" disabled class="mal-text">സോൺ തിരഞ്ഞെടുക്കുക</option>
        <option v-for="z in zones" :key="'z-'+z.id" :value="String(z.id)">{{ z.name }}</option>
      </select>
      <small v-if="zonesLoading" class="text-muted mal-text d-block mt-1">സോണുകൾ ലോഡ് ചെയ്യുന്നു...</small>
    </div>

    <div v-if="showAdminUnitPicker" class="form-group">
      <label class="form-label mal-text">യൂണിറ്റ് (Unit) <span class="text-danger">*</span></label>
      <select v-model="selectedUnitId" class="form-control" :disabled="unitsLoading || !zoneId">
        <option value="" disabled class="mal-text">യൂണിറ്റ് തിരഞ്ഞെടുക്കുക</option>
        <option v-for="u in zoneUnits" :key="'au-'+u.id" :value="String(u.id)">{{ u.name }}</option>
      </select>
      <small v-if="unitsLoading" class="text-muted mal-text d-block mt-1">യൂണിറ്റുകൾ ലോഡ് ചെയ്യുന്നു...</small>
    </div>

    <!-- Zone admin: pick unit before adding (all units in zone listed below) -->
    <div v-if="showZoneUnitPicker" class="form-group">
      <label class="form-label mal-text">യൂണിറ്റ് (Unit) <span class="text-danger">*</span></label>
      <select v-model="selectedUnitId" class="form-control">
        <option value="" disabled class="mal-text">യൂണിറ്റ് തിരഞ്ഞെടുക്കുക</option>
        <option v-for="u in zoneUnits" :key="'zu-'+u.id" :value="String(u.id)">{{ u.name }}</option>
      </select>
      <small v-if="unitsLoading" class="text-muted mal-text d-block mt-1">യൂണിറ്റുകൾ ലോഡ് ചെയ്യുന്നു...</small>
    </div>

    <div v-if="canAddCenter && isQhlsWingUser && canShowAddButton" class="add-btn-wrap">
      <button type="button" class="btn btn-primary btn-block add-btn" @click="openAddModal">
        <ion-icon name="add-outline"></ion-icon>
        <span class="mal-text">ചേർക്കുക (Add)</span>
      </button>
    </div>

    <div v-if="!isQhlsWingUser" class="empty-note mal-text">
      ഈ പേജ് വിംഗ് 1 (വിസ്ഡം), 2 (യൂത്ത്), 3 (സ്റ്റുഡൻ്റ്) അഡ്മിനുകൾക്ക് മാത്രം ലഭ്യമാണ്.
    </div>

    <div v-else>
      <div v-if="loading" class="text-center py-4">
        <div class="spinner-border text-primary" role="status"></div>
      </div>

      <div v-else-if="savedCenters.length > 0" class="saved-centers-section">
        <h4 class="section-title mal-text">പരീക്ഷാ കേന്ദ്രങ്ങൾ</h4>
        <div class="saved-centers-list">
          <div v-for="(center, idx) in savedCenters" :key="center.id || idx" class="saved-center-card">
            <div class="center-info">
              <strong class="center-name">{{ getCenterName(center) }}</strong>
              <span class="center-detail" v-if="getCenterTypeLabel(center)">തരം: {{ getCenterTypeLabel(center) }}</span>
              <span class="center-detail">കോഡിനേറ്റർ: {{ center.convenor_name }}</span>
              <span class="convenor-phone">
                <a v-if="center.convenor_phone" :href="'tel:' + center.convenor_phone">{{ center.convenor_phone }}</a>
                <template v-else>{{ center.convenor_phone || '-' }}</template>
              </span>
              <span class="center-detail" v-if="center.invigilator_name">
                <ion-icon name="person-outline" style="vertical-align: -2px; margin-right: 3px;"></ion-icon>
                ഇൻവിജിലേറ്റർ: {{ center.invigilator_name }}
              </span>
            </div>
            <div class="center-actions-wrap">
              <div class="center-actions" v-if="isInvigilatorForCenter(center)">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="exportCenterCandidatesPdf(center)"
                  :disabled="isCenterPdfDownloading(center)"
                >
                  {{ isCenterPdfDownloading(center) ? 'Downloading...' : 'Candidates PDF' }}
                </button>
              </div>
              <div class="center-actions" v-if="canAddCenter">
                <button type="button" class="btn btn-sm btn-outline-primary" @click="openEditModal(center)">എഡിറ്റ്</button>
                <button type="button" class="btn btn-sm btn-outline-danger" @click="confirmDelete(center)">ഡിലീറ്റ്</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-note mal-text">ഇതുവരെ പരീക്ഷാ കേന്ദ്രങ്ങൾ ചേർത്തിട്ടില്ല.</div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="formModalOpen" class="modal-overlay action-sheet-open" @click.self="closeFormModal">
      <div class="modal-sheet">
        <div class="modal-sheet-handle"></div>
        <h5 class="modal-title mal-text">{{ editingId ? 'പരീക്ഷാ കേന്ദ്രം എഡിറ്റ് ചെയ്യുക' : 'പരീക്ഷാ കേന്ദ്രം ചേർക്കുക' }}</h5>
        <form @submit.prevent="submitFormModal">
          <div class="form-group">
            <label class="form-label mal-text">പരീക്ഷാ കേന്ദ്രം പേര് (Center Name) <span class="text-danger">*</span></label>
            <input v-model="form.madrasa_name" type="text" class="form-control" placeholder="പരീക്ഷാ കേന്ദ്രത്തിന്റെ പേര്" required />
          </div>
          <div class="form-group">
            <label class="form-label mal-text">Type of Centre <span class="text-danger">*</span></label>
            <select v-model="form.center_type" class="form-control" required>
              <option value="" disabled class="mal-text">തിരഞ്ഞെടുക്കുക</option>
              <option v-for="opt in centerTypeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label mal-text">സെൻ്റർ കോഡിനേറ്ററുടെ പേര്? <span class="text-danger">*</span></label>
            <input v-model="form.convenor_name" type="text" class="form-control" placeholder="കോഡിനേറ്ററുടെ പേര്" required />
          </div>
          <div class="form-group">
            <label class="form-label mal-text">സെൻ്റർ കോഡിനേറ്ററുടെ നമ്പർ? <span class="text-danger">*</span></label>
            <input v-model="form.convenor_phone" type="tel" class="form-control" placeholder="10 അക്ക മൊബൈൽ നമ്പർ" maxlength="10" inputmode="numeric" required />
          </div>
          <div class="form-group">
            <label class="form-label mal-text">Exam Start Time <span class="text-danger">*</span></label>
            <input v-model="form.exam_start_time" type="time" class="form-control" required />
          </div>
          <div class="form-group">
            <label class="form-label mal-text">Exam End Time <span class="text-danger">*</span></label>
            <input v-model="form.exam_end_time" type="time" class="form-control" required />
          </div>
          <div class="form-group invigilator-group">
            <label class="form-label mal-text">ഇൻവിജിലേറ്റർ (Invigilator)</label>
            <div class="typehead-wrapper">
              <input
                type="text"
                class="form-control"
                v-model="invigilatorSearch"
                placeholder="പേര് ടൈപ്പ് ചെയ്ത് തിരയുക..."
                autocomplete="off"
                @input="onInvigilatorInput"
                @blur="onInvigilatorBlur"
                @focus="onInvigilatorFocus"
              />
              <div v-if="invigilatorLoading" class="typehead-loading mal-text">തിരയുന്നു...</div>
              <ul v-else-if="invigilatorSuggestions.length > 0 && invigilatorDropdownOpen" class="typehead-dropdown">
                <li
                  v-for="m in invigilatorSuggestions"
                  :key="m.value"
                  class="typehead-item"
                  @mousedown.prevent="selectInvigilator(m)"
                >
                  <span class="typehead-name">{{ m.name }}</span>
                  <span class="typehead-mobile" v-if="m.mobile">{{ m.mobile }}</span>
                </li>
              </ul>
              <div v-else-if="invigilatorSearched && !invigilatorLoading && invigilatorSuggestions.length === 0 && invigilatorDropdownOpen" class="typehead-empty mal-text">
                കണ്ടെത്തിയില്ല
              </div>
            </div>
            <small v-if="form.invigilator_name" class="invigilator-selected-hint mal-text">
              <ion-icon name="checkmark-circle" style="vertical-align: -2px; color: #16a34a;"></ion-icon>
              {{ form.invigilator_name }}
            </small>
            <small v-else class="text-muted mal-text" style="font-size: 11px;">
              ലിസ്റ്റിൽ നിന്ന് തിരഞ്ഞെടുക്കുക (must select from list)
            </small>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-secondary" @click="closeFormModal">ക്യാൻസൽ</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'സേവ് ചെയ്യുന്നു...' : 'സേവ് ചെയ്യുക' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete confirm modal -->
    <div v-if="deleteModalOpen" class="modal-overlay" @click.self="deleteModalOpen = false">
      <div class="modal-box modal-confirm">
        <h5 class="modal-title mal-text">ഡിലീറ്റ് ചെയ്യണോ?</h5>
        <p class="mal-text" v-if="centerToDelete">{{ getCenterName(centerToDelete) }} - ഈ പരീക്ഷാ കേന്ദ്രം ഡിലീറ്റ് ചെയ്യുമോ?</p>
        <div class="modal-actions">
          <button type="button" class="btn btn-secondary" @click="deleteModalOpen = false">ക്യാൻസൽ</button>
          <button type="button" class="btn btn-danger" :disabled="deleteSaving" @click="submitDelete">{{ deleteSaving ? 'ഡിലീറ്റ് ചെയ്യുന്നു...' : 'ഡിലീറ്റ്' }}</button>
        </div>
      </div>
    </div>

    <!-- Alert -->
    <div class="modal fade action-sheet" id="actionSheetAlert" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-body">
            <div class="action-sheet-content">
              <div class="iconbox text-success" v-if="alertClass === 'alert-success'">
                <ion-icon name="checkmark-circle"></ion-icon>
              </div>
              <div class="iconbox text-danger" v-if="alertClass === 'alert-error'">
                <ion-icon name="close-circle"></ion-icon>
              </div>
              <div class="text-center p-2">
                <h3>{{ alertClass === 'alert-success' ? ($t('success') || 'Success') : ($t('error') || 'Error') }}</h3>
                <p class="mal-text text-primary">{{ alertMessage }}</p>
              </div>
              <a href="javascript:void(0)" class="btn btn-primary btn-lg btn-block" @click="closeAlertModal">
                {{ $t('close') || 'Close' }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserService from '../services/user.service';
import $ from 'jquery';

export default {
  name: 'QHLSExamCenter',
  data() {
    return {
      loading: false,
      saving: false,
      deleteSaving: false,
      savedCenters: [],
      districts: [],
      zones: [],
      districtsLoading: false,
      zonesLoading: false,
      zoneUnits: [],
      unitsLoading: false,
      selectedUnitId: '',
      selectedDistrictId: '',
      selectedZoneId: '',
      editingCenter: null,
      formModalOpen: false,
      deleteModalOpen: false,
      editingId: null,
      centerToDelete: null,
      form: {
        madrasa_name: '',
        center_type: '',
        convenor_name: '',
        convenor_phone: '',
        exam_start_time: '',
        exam_end_time: '',
        invigilator_name: '',
        invigilator_member_id: null,
      },
      invigilatorSearch: '',
      invigilatorSuggestions: [],
      invigilatorLoading: false,
      invigilatorDropdownOpen: false,
      invigilatorSearched: false,
      invigilatorDebounceTimer: null,
      centerPdfDownloading: {},
      centerTypeOptions: [
        { value: 'masjid', label: 'Masjid' },
        { value: 'madrasa', label: 'Madrasa' },
        { value: 'school', label: 'School' },
        { value: 'rental_room', label: 'Rental Room' },
      ],
      alertMessage: '',
      alertClass: 'alert-success',
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentMemberId() {
      const raw = this.currentUser?.member_id;
      if (raw == null || raw === '') return null;
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? null : n;
    },
    /** Matches router `/org/:sub_level/:id/...` (2=district, 3=zone, 4=unit). */
    routeSubLevel() {
      const raw = this.$route.params?.sub_level;
      const n = parseInt(String(raw ?? ''), 10);
      return Number.isNaN(n) ? null : n;
    },
    routeEntityId() {
      const raw = this.$route.params?.id;
      if (raw == null || raw === '') return null;
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? null : n;
    },
    zoneId() {
      const sl = this.routeSubLevel;
      if (sl === 3 && this.routeEntityId != null) return this.routeEntityId;
      if ((this.userLevel === 1 || this.userLevel === 2) && this.selectedZoneId) {
        const n = parseInt(String(this.selectedZoneId), 10);
        return Number.isNaN(n) ? null : n;
      }
      const z = this.currentUser?.zone_id;
      if (z == null) return null;
      const n = parseInt(String(z), 10);
      return Number.isNaN(n) ? null : n;
    },
    /** Unit filter for GET list: null = all units in zone (zone admin route). */
    unitId() {
      const sl = this.routeSubLevel;
      if (sl === 4 && this.routeEntityId != null) return this.routeEntityId;
      if (this.userLevel === 3 && sl === 3) return null;
      const u = this.currentUser?.unit_id;
      if (u == null) return null;
      const n = parseInt(String(u), 10);
      return Number.isNaN(n) ? null : n;
    },
    districtId() {
      return this.currentUser?.district_id || null;
    },
    userLevel() {
      const raw = this.currentUser?.user_level;
      if (raw == null) return 0;
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? 0 : n;
    },
    /** Wings: 1=Wisdom (Org), 2=Youth, 3=Students — all may manage QHLS centers here. */
    isQhlsWingUser() {
      const w = this.currentUser?.wing_id ?? this.currentUser?.wing ?? this.currentUser?.wing_type;
      const n = parseInt(String(w ?? ''), 10);
      return n === 1 || n === 2 || n === 3;
    },
    isUnitRoute() {
      return this.routeSubLevel === 4;
    },
    showStateDistrictPickers() {
      // When opened from unit route, district/zone/unit are implied by the unit context.
      return this.isQhlsWingUser && !this.isUnitRoute && (this.userLevel === 1 || this.userLevel === 2);
    },
    effectiveDistrictId() {
      if (this.userLevel === 2) {
        if (this.routeSubLevel === 2 && this.routeEntityId != null) return this.routeEntityId;
        const d = this.currentUser?.district_id;
        const n = d != null ? parseInt(String(d), 10) : null;
        return n && !Number.isNaN(n) ? n : null;
      }
      if (this.userLevel === 1) {
        const n = parseInt(String(this.selectedDistrictId || ''), 10);
        return Number.isNaN(n) ? null : n;
      }
      return null;
    },
    showAdminUnitPicker() {
      return this.isQhlsWingUser && !this.isUnitRoute && (this.userLevel === 1 || this.userLevel === 2) && !!this.zoneId;
    },
    showZoneUnitPicker() {
      return this.userLevel === 3 && !this.isUnitRoute && this.routeSubLevel === 3 && !!this.zoneId;
    },
    needsUnitBeforeAdd() {
      return this.showZoneUnitPicker || this.showAdminUnitPicker;
    },
    canShowAddButton() {
      if (!this.needsUnitBeforeAdd) return true;
      const n = parseInt(String(this.selectedUnitId || ''), 10);
      return !Number.isNaN(n) && n > 0;
    },
    /** unit_id for create / fallback for update */
    storePayloadUnitId() {
      if (this.routeSubLevel === 4 && this.routeEntityId != null) return this.routeEntityId;
      if (this.showZoneUnitPicker) {
        const n = parseInt(String(this.selectedUnitId || ''), 10);
        return Number.isNaN(n) ? null : n;
      }
      const u = this.currentUser?.unit_id;
      if (u == null) return null;
      const n = parseInt(String(u), 10);
      return Number.isNaN(n) ? null : n;
    },
    canAddCenter() {
      return (
        this.userLevel == 1
        || this.userLevel == 2
        || this.userLevel == 3
        || this.userLevel == 4
      );
    },
    canLoadQhlsData() {
      return this.canAddCenter && this.zoneId && this.isQhlsWingUser;
    },
    typeheadUnitId() {
      if (this.editingId && this.editingCenter) {
        const raw = this.editingCenter.unit_id ?? this.editingCenter.unit?.id;
        if (raw != null && raw !== '') {
          const n = parseInt(String(raw), 10);
          if (!Number.isNaN(n)) return n;
        }
      }
      return this.storePayloadUnitId;
    },
  },
  watch: {
    '$route.params.id'(newVal, oldVal) {
      if (newVal !== oldVal && this.canLoadQhlsData) {
        this.bootstrapQhls();
      }
    },
    '$route.params.sub_level'(newVal, oldVal) {
      if (newVal !== oldVal && this.canLoadQhlsData) {
        this.bootstrapQhls();
      }
    },
    selectedDistrictId(newVal, oldVal) {
      if (newVal !== oldVal && this.userLevel === 1) {
        this.selectedZoneId = '';
        this.selectedUnitId = '';
        this.fetchZonesForDistrict();
      }
    },
    selectedZoneId(newVal, oldVal) {
      if (newVal !== oldVal && (this.userLevel === 1 || this.userLevel === 2)) {
        this.selectedUnitId = '';
        this.fetchZoneUnits();
        this.fetchSavedCenters();
      }
    },
  },
  mounted() {
    this.$root.updateParent('QHLS പരീക്ഷാ കേന്ദ്രം');
    this.bootstrapQhls();
  },
  methods: {
    isInvigilatorForCenter(center) {
      if (!center) return false;
      if (!this.currentMemberId) return false;
      const cid = parseInt(String(center.invigilator_member_id || ''), 10);
      return !Number.isNaN(cid) && cid === this.currentMemberId;
    },
    isCenterPdfDownloading(center) {
      const id = center?.id;
      return !!(id && this.centerPdfDownloading && this.centerPdfDownloading[id]);
    },
    async bootstrapQhls() {
      if (!this.canLoadQhlsData) return;
      if (this.showStateDistrictPickers) {
        await this.setupStateDistrictPickers();
      }
      if (this.showZoneUnitPicker) {
        await this.fetchZoneUnits();
        if (this.zoneUnits.length === 1) {
          this.selectedUnitId = String(this.zoneUnits[0].id);
        }
      } else {
        if (!this.showAdminUnitPicker) {
          this.selectedUnitId = '';
          this.zoneUnits = [];
        }
      }
      await this.fetchSavedCenters();
    },
    async setupStateDistrictPickers() {
      if (this.userLevel === 2) {
        this.selectedDistrictId = this.effectiveDistrictId ? String(this.effectiveDistrictId) : '';
        await this.fetchZonesForDistrict();
        if (!this.selectedZoneId && this.zones.length === 1) this.selectedZoneId = String(this.zones[0].id);
        return;
      }
      if (this.userLevel === 1) {
        if (this.districts.length === 0) await this.fetchDistricts();
        if (!this.selectedDistrictId && this.districts.length === 1) this.selectedDistrictId = String(this.districts[0].id);
        if (this.effectiveDistrictId) {
          await this.fetchZonesForDistrict();
          if (!this.selectedZoneId && this.zones.length === 1) this.selectedZoneId = String(this.zones[0].id);
        }
      }
    },
    async fetchDistricts() {
      this.districtsLoading = true;
      this.districts = [];
      try {
        const res = await UserService.getSubLevels('get-sub-levels', { sub_level_type: 2, id: 0 });
        this.districts = res.data?.data?.list || [];
      } catch (e) {
        this.districts = [];
      } finally {
        this.districtsLoading = false;
      }
    },
    async fetchZonesForDistrict() {
      const districtId = this.effectiveDistrictId;
      if (!districtId) return;
      this.zonesLoading = true;
      this.zones = [];
      try {
        const res = await UserService.getSubLevels('get-sub-levels', { sub_level_type: 3, id: districtId });
        this.zones = res.data?.data?.list || [];
      } catch (e) {
        this.zones = [];
      } finally {
        this.zonesLoading = false;
      }
    },
    async fetchZoneUnits() {
      if (!this.zoneId) return;
      this.unitsLoading = true;
      this.zoneUnits = [];
      try {
        const res = await UserService.getSubLevels('get-sub-levels', { sub_level_type: 4, id: this.zoneId });
        this.zoneUnits = res.data?.data?.list || [];
      } catch (e) {
        this.zoneUnits = [];
      } finally {
        this.unitsLoading = false;
      }
    },
    async fetchSavedCenters() {
      if (!this.zoneId) return;
      try {
        this.loading = true;
        const params = { zone_id: this.zoneId };
        if (this.unitId) params.unit_id = this.unitId;
        const response = await UserService.authGetRequest('qhls-exam-centers', params);
        if (response.data.status === 'success' && Array.isArray(response.data.data?.list)) {
          this.savedCenters = response.data.data.list;
        } else {
          this.savedCenters = [];
        }
      } catch (err) {
        console.warn('Could not load saved centers:', err);
        this.savedCenters = [];
      } finally {
        this.loading = false;
      }
    },
    getCenterName(center) {
      if (center.madrasa_name) return center.madrasa_name;
      if (center.madrasa && center.madrasa.name) return center.madrasa.name;
      return (center.convenor_name || '');
    },
    async exportCenterCandidatesPdf(center) {
      if (!center?.id) return;
      const id = center.id;
      this.centerPdfDownloading = { ...(this.centerPdfDownloading || {}), [id]: true };
      try {
        const response = await UserService.authPostRequest(
          'qhls-exam-center-candidates-pdf',
          { center_id: id },
          { responseType: 'blob' }
        );

        const blob = response.data;
        const contentType = (response.headers && response.headers['content-type']) || '';
        if (contentType.indexOf('application/json') !== -1) {
          const text = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsText(blob);
          });
          const json = JSON.parse(text);
          alert(json.message || 'Download failed');
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qhls-center-candidates-${id}-${new Date().toISOString().slice(0, 10)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        let msg = 'Failed to download PDF';
        if (err?.response?.data && typeof err.response.data === 'object' && !(err.response.data instanceof Blob)) {
          msg = err.response.data.message || msg;
        } else if (err?.message) {
          msg = err.message;
        }
        alert(msg);
      } finally {
        this.centerPdfDownloading = { ...(this.centerPdfDownloading || {}), [id]: false };
      }
    },
    normalizeCenterType(raw) {
      if (raw == null || raw === '') return '';
      const s = String(raw).trim().toLowerCase().replace(/\s+/g, '_');
      const map = {
        masjid: 'masjid',
        madrasa: 'madrasa',
        school: 'school',
        rental_room: 'rental_room',
        rentalroom: 'rental_room',
        'rental room': 'rental_room',
      };
      return map[s] || (['masjid', 'madrasa', 'school', 'rental_room'].includes(s) ? s : '');
    },
    getCenterTypeLabel(center) {
      const v = this.normalizeCenterType(center?.center_type ?? center?.centre_type ?? center?.type_of_centre);
      const opt = this.centerTypeOptions.find((o) => o.value === v);
      return opt ? opt.label : '';
    },
    openAddModal() {
      this.editingId = null;
      this.editingCenter = null;
      this.form = { madrasa_name: '', center_type: '', convenor_name: '', convenor_phone: '', exam_start_time: '', exam_end_time: '', invigilator_name: '', invigilator_member_id: null };
      this.resetInvigilator();
      this.formModalOpen = true;
    },
    openEditModal(center) {
      this.editingId = center.id;
      this.editingCenter = center;
      this.form = {
        madrasa_name: this.getCenterName(center),
        center_type: this.normalizeCenterType(center.center_type ?? center.centre_type ?? center.type_of_centre),
        convenor_name: center.convenor_name || '',
        convenor_phone: (center.convenor_phone || '').replace(/\D/g, ''),
        exam_start_time: center.exam_start_time ? (String(center.exam_start_time).length > 5 ? String(center.exam_start_time).substring(0, 5) : center.exam_start_time) : '',
        exam_end_time: center.exam_end_time ? (String(center.exam_end_time).length > 5 ? String(center.exam_end_time).substring(0, 5) : center.exam_end_time) : '',
        invigilator_name: center.invigilator_name || '',
        invigilator_member_id: center.invigilator_member_id || null,
      };
      this.invigilatorSearch = center.invigilator_name || '';
      this.invigilatorSuggestions = [];
      this.invigilatorDropdownOpen = false;
      this.invigilatorSearched = false;
      this.formModalOpen = true;
    },
    closeFormModal() {
      this.formModalOpen = false;
      this.editingId = null;
      this.editingCenter = null;
      this.resetInvigilator();
    },
    resetInvigilator() {
      this.invigilatorSearch = '';
      this.invigilatorSuggestions = [];
      this.invigilatorLoading = false;
      this.invigilatorDropdownOpen = false;
      this.invigilatorSearched = false;
      if (this.invigilatorDebounceTimer) clearTimeout(this.invigilatorDebounceTimer);
    },
    onInvigilatorInput() {
      const q = (this.invigilatorSearch || '').trim();
      // Clear saved selection if user types differently from the saved name
      if (this.form.invigilator_name && q !== this.form.invigilator_name) {
        this.form.invigilator_name = '';
        this.form.invigilator_member_id = null;
      }
      this.invigilatorDropdownOpen = true;
      if (this.invigilatorDebounceTimer) clearTimeout(this.invigilatorDebounceTimer);
      if (q.length < 2) {
        this.invigilatorSuggestions = [];
        this.invigilatorSearched = false;
        return;
      }
      this.invigilatorDebounceTimer = setTimeout(() => this.fetchInvigilators(q), 300);
    },
    onInvigilatorFocus() {
      if (this.invigilatorSuggestions.length > 0) {
        this.invigilatorDropdownOpen = true;
      }
    },
    onInvigilatorBlur() {
      setTimeout(() => { this.invigilatorDropdownOpen = false; }, 200);
      // If no member was selected from the dropdown, clear the search text
      if (!this.form.invigilator_name) {
        this.invigilatorSearch = '';
      }
    },
    async fetchInvigilators(q) {
      const unitId = this.typeheadUnitId;
      if (!unitId) return;
      this.invigilatorLoading = true;
      this.invigilatorSearched = true;
      try {
        const response = await UserService.authPostRequest('get-typehead-values', {
          q,
          type: 0,
          filter: 'inside',
          unit_id: unitId,
          user_level: 4,
        });
        if (response.data.status === 'success' && Array.isArray(response.data.data?.list)) {
          this.invigilatorSuggestions = response.data.data.list;
        } else {
          this.invigilatorSuggestions = [];
        }
      } catch (e) {
        this.invigilatorSuggestions = [];
      } finally {
        this.invigilatorLoading = false;
        this.invigilatorDropdownOpen = true;
      }
    },
    selectInvigilator(member) {
      this.form.invigilator_name = member.name;
      this.form.invigilator_member_id = member.value || member.id || null;
      this.invigilatorSearch = member.name;
      this.invigilatorSuggestions = [];
      this.invigilatorDropdownOpen = false;
    },
    showAlert(msg, type = 'success') {
      this.alertMessage = msg;
      this.alertClass = type === 'error' ? 'alert-error' : 'alert-success';
      this.$nextTick(() => {
        $('#actionSheetAlert').modal('show');
      });
    },
    closeAlertModal() {
      $('#actionSheetAlert').modal('hide');
      this.alertMessage = '';
      this.alertClass = 'alert-success';
    },
    validatePhone(phone) {
      const p = String(phone || '').replace(/\D/g, '');
      return p.length === 10;
    },
    resolveUnitIdForSave() {
      if (this.editingId && this.editingCenter) {
        const raw = this.editingCenter.unit_id ?? this.editingCenter.unitId ?? this.editingCenter.unit?.id;
        if (raw != null && raw !== '') {
          const n = parseInt(String(raw), 10);
          if (!Number.isNaN(n)) return n;
        }
      }
      return this.storePayloadUnitId;
    },
    async submitFormModal() {
      if (!this.canAddCenter || !this.isQhlsWingUser) {
        this.showAlert('ഈ നടപടി അനുവദനീയമല്ല', 'error');
        return;
      }
      if (!this.form.madrasa_name?.trim() || !this.form.center_type || !this.form.convenor_name || !this.form.convenor_phone || !this.form.exam_start_time || !this.form.exam_end_time) {
        this.showAlert('എല്ലാ ഫീൽഡുകളും പൂരിപ്പിക്കുക', 'error');
        return;
      }
      if (!this.validatePhone(this.form.convenor_phone)) {
        this.showAlert('സാധുവായ 10 അക്ക ഫോൺ നമ്പർ നൽകുക', 'error');
        return;
      }
      if (!this.zoneId) {
        this.showAlert('സോൺ വിവരം കണ്ടെത്തിയില്ല', 'error');
        return;
      }
      const saveUnitId = this.resolveUnitIdForSave();
      if (!this.editingId && this.needsUnitBeforeAdd && !saveUnitId) {
        this.showAlert('ചേർക്കാൻ യൂണിറ്റ് തിരഞ്ഞെടുക്കുക', 'error');
        return;
      }
      if (!this.editingId && !saveUnitId) {
        this.showAlert('യൂണിറ്റ് വിവരം കണ്ടെത്തിയില്ല', 'error');
        return;
      }
      try {
        this.saving = true;
        const madrasaName = this.form.madrasa_name.trim();

        if (this.editingId) {
          const response = await UserService.authPostRequest(`qhls-exam-center/${this.editingId}`, {
            madrasa_id: null,
            madrasa_name: madrasaName,
            center_type: this.form.center_type,
            convenor_name: this.form.convenor_name?.trim(),
            convenor_phone: this.form.convenor_phone.trim().replace(/\D/g, ''),
            exam_start_time: this.form.exam_start_time || null,
            exam_end_time: this.form.exam_end_time || null,
            invigilator_name: this.form.invigilator_name?.trim() || null,
            invigilator_member_id: this.form.invigilator_member_id || null,
            unit_id: saveUnitId || null,
          });
          if (response.data.status === 'success') {
            this.showAlert('വിജയകരമായി അപ്ഡേറ്റ് ചെയ്തു');
            this.closeFormModal();
            this.fetchSavedCenters();
          } else {
            this.showAlert(response.data.message || 'അപ്ഡേറ്റ് ചെയ്യുന്നതിൽ പിശക്', 'error');
          }
        } else {
          const payload = {
            madrasa_id: null,
            madrasa_name: madrasaName,
            center_type: this.form.center_type,
            convenor_name: this.form.convenor_name?.trim(),
            name: this.form.convenor_name?.trim(),
            convenor_phone: this.form.convenor_phone.trim().replace(/\D/g, ''),
            exam_start_time: this.form.exam_start_time || null,
            exam_end_time: this.form.exam_end_time || null,
            invigilator_name: this.form.invigilator_name?.trim() || null,
            invigilator_member_id: this.form.invigilator_member_id || null,
            zone_id: this.zoneId,
            unit_id: saveUnitId || null,
          };
          const response = await UserService.authPostRequest('qhls-exam-center/store', payload);
          if (response.data.status === 'success') {
            this.showAlert('വിജയകരമായി സേവ് ചെയ്തു');
            this.closeFormModal();
            this.fetchSavedCenters();
          } else {
            this.showAlert(response.data.message || 'സേവ് ചെയ്യുന്നതിൽ പിശക്', 'error');
          }
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'സേവ് ചെയ്യുന്നതിൽ പിശക്';
        this.showAlert(msg, 'error');
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(center) {
      this.centerToDelete = center;
      this.deleteModalOpen = true;
    },
    async submitDelete() {
      if (!this.centerToDelete) return;
      try {
        this.deleteSaving = true;
        const response = await UserService.authPostRequest(`qhls-exam-center-delete/${this.centerToDelete.id}`, {});
        if (response.data.status === 'success') {
          this.showAlert('വിജയകരമായി ഡിലീറ്റ് ചെയ്തു');
          this.deleteModalOpen = false;
          this.centerToDelete = null;
          this.fetchSavedCenters();
        } else {
          this.showAlert(response.data.message || 'ഡിലീറ്റ് ചെയ്യുന്നതിൽ പിശക്', 'error');
        }
      } catch (err) {
        this.showAlert(err.response?.data?.message || 'ഡിലീറ്റ് ചെയ്യുന്നതിൽ പിശക്', 'error');
      } finally {
        this.deleteSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.qhls-exam-center {
  max-width: 480px;
  margin: 0 auto;
  padding: 16px;
}
.page-header {
  margin-bottom: 20px;
}
.page-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
}
.add-btn-wrap {
  margin-bottom: 16px;
}
.add-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
}
.add-btn ion-icon {
  font-size: 1.25rem;
}
.saved-centers-section {
  margin-top: 8px;
}
.section-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1a418c;
}
.saved-centers-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.saved-center-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.center-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}
.center-info .center-name {
  font-size: 1rem;
  color: #1a418c;
}
.center-info .center-detail {
  font-size: 0.9rem;
  color: #6c757d;
}
.center-info .convenor-phone {
  font-size: 0.9rem;
  color: #1a418c;
}
.center-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.center-actions-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}
.empty-note {
  margin-top: 18px;
  text-align: center;
  color: #6c757d;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 9999;
  padding: 0;
}
.modal-overlay.action-sheet-open {
  align-items: flex-end;
}
.modal-sheet {
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 20px;
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}
.modal-sheet-handle {
  width: 36px;
  height: 4px;
  background: #dee2e6;
  border-radius: 2px;
  margin: 0 auto 16px;
}
.modal-sheet .modal-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1a418c;
}
.form-group {
  margin-bottom: 14px;
}
.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
}
.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.modal-sheet .modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}
.modal-box {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  max-width: 400px;
  width: 100%;
  margin: 16px;
}
.modal-box.modal-confirm .modal-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
}
.modal-box .modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 16px;
}
.modal-confirm p {
  margin-bottom: 16px;
  font-size: 0.95rem;
  color: #333;
}
@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
.invigilator-group {
  position: relative;
}
.typehead-wrapper {
  position: relative;
}
.typehead-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 8px 8px;
  list-style: none;
  margin: 0;
  padding: 0;
  z-index: 100;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}
.typehead-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  gap: 8px;
}
.typehead-item:last-child {
  border-bottom: none;
}
.typehead-item:hover, .typehead-item:active {
  background: rgba(26, 65, 140, 0.08);
}
.typehead-name {
  font-size: 14px;
  color: #1a418c;
  font-weight: 500;
  flex: 1;
}
.typehead-mobile {
  font-size: 12px;
  color: #6c757d;
  white-space: nowrap;
}
.typehead-loading, .typehead-empty {
  padding: 10px 12px;
  font-size: 13px;
  color: #6c757d;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 8px 8px;
  background: #fff;
}
.invigilator-selected-hint {
  display: block;
  margin-top: 5px;
  font-size: 12px;
  color: #15803d;
  font-weight: 500;
}
</style>
